import { useCallback, useEffect, useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { renderSettingsResourceLabel } from "../../../utils/helpers/settings";
import { useFields } from "../api";

const columnHelper = createColumnHelper();

export const useColumns = ({ config, extraColumns }) => {
  const { t } = useTranslation();

  const { fields, getFields } = useFields();

  useEffect(() => {
    const controller = new AbortController();
    getFields(controller);
    return () => controller.abort();
  }, [getFields]);

  const getExtraColumnsConfig = useCallback((name) => {
    const keys = Object.keys(extraColumns?.[name] || {});

    return keys.map((key) => ({
      isGroup: true,
      header: key,
      columns: (extraColumns?.[name]?.[key] || [])?.map((col) => ({
        name: col.name,
        header: col?.alias || col?.display_name || t((col?.name || "").replaceAll(/[_\\.]/g, ' '))
      }))
    }));
  }, [extraColumns, t]);

  const mergedConfig = useMemo(() => {
    if (!fields || !config) return [];

    const formattedFields = fields?.map((field) => field?.fields?.map(item => ({ ...item, table: field?.table })))?.flat();

    let columns = config.map((column) => {
      const [type, name] = column.name.split(".");

      const matchingField = formattedFields?.find(
        (fieldGroup) => fieldGroup?.table === type && fieldGroup?.name === name
      );

      return {
        ...column,
        display_name: column.display_name || matchingField?.display_name || name,
        fieldType: matchingField?.fieldType,
        options: matchingField?.options,
      };
    });

    columns = columns.flatMap((column) =>
      (column.hasExtraColumns && extraColumns?.[column.name])
        ? (Array.isArray(extraColumns[column.name])
          ? extraColumns[column.name]
          : getExtraColumnsConfig(column.name))
        : [column]
    );

    return columns.filter((column) => !column?.hasExtraColumns);
  }, [fields, config, extraColumns, getExtraColumnsConfig]);

  return useMemo(() => {
    return mergedConfig?.map((column) => {
      if (column.isGroup) {
        return columnHelper.group({
          id: column.header,
          header: () => <div className="d-flex w-100 justify-content-center">
            {column.header}
          </div>,
          columns: column.columns.map((item) => (
            columnHelper.accessor(item.name, {
              header: item?.header
            })
          ))
        });
      } else {
        return columnHelper.accessor(column?.dataIndex || (column?.aggregation ? `${(column?.name || "").replaceAll(".", "_")}_${column?.aggregation}` : (column?.name || "").replaceAll(".", "_")), {
          header: column?.alias || column?.display_name || t((column?.name || "").replaceAll(/[_\\.]/g, ' ')),
          cell: info => {
            const value = info.getValue();
            if (typeof value === 'object') {
              return renderSettingsResourceLabel(value);
            }
            return value !== null ? value : '';
          },
        })
      }
    });
  }, [mergedConfig, t])
}
